import React, {useState, useEffect, MouseEventHandler} from 'react';
import {IComment} from '../../model/commentModel';
import CommentHeader from './CommentHeader';
import CommentBody from './CommentBody';
import styled from 'styled-components';
import Constants, {AnnotationModes, ServiceNames} from '../../constants/Constants';
import {TscStyles, ControlButton, TscButtonType, Glyphs, TscButtonGlyphPlacement, TscThemeName, TscThemeNames, Spinner} from '@techsmith/tsc-cloud-style-guide';
import TimeFormatter from '../../util/TimeFormatter';
import {useTranslation} from 'react-i18next';
import CommentInput from './CommentInput';
import conversationApi from '../../service/conversationApi';
import {mediaInterface} from '@techsmith/techsmith-smartplayer';
import DeleteCommentAlert from './DeleteCommentAlert';
import {scrollCommentIntoView} from '../../util/CommentScroller';
import {SetupWorkerState} from '../../model/workerState';
import config from '../../service/config';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {commentsStore, updateCommentsState} from '../../context/commentsProvider';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import {themeStore} from '../../context/themeProvider';
import {quizDataStore} from '../../context/quizDataProvider';
import {mediaStore} from '../../context/mediaProvider';
import logging from '../../service/logging';

export const CommentWrapper = styled.div<{theme: TscThemeNames; isReply: boolean; isExpanded: boolean; isSelected: boolean; isHighlighted: boolean}>`
   @keyframes highlight-comment {
      0%, 100% {
         box-shadow: transparent 0 0 0 2px inset, rgba(0, 0, 20, 0.1) 0 5px 10px;
      }
      30%, 70% {
         box-shadow: rgb(54, 190, 95) 0 0 0 2px inset, rgba(0, 0, 20, 0.1) 0 5px 10px;
      }
   }

   margin: ${props => props.isReply ? '0 0.5rem 0 0.5rem' : '.5rem 1rem 0 1rem'};
   padding: ${props => props.isReply ? '0.625rem 0.625rem 0 0.625rem' : '0.625rem'};
   background-color: ${
   props => props.isReply ? 'transparent' :
      props.theme === TscThemeName.dusk && !props.isSelected ? TscStyles.color.ui.dusk.mediumDark :
         props.theme === TscThemeName.dawn && !props.isSelected ? TscStyles.color.ui.dawn.medium :
            props.theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk :
               TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props => props.isReply ? 'transparent' :
      props.theme === TscThemeName.dusk && !props.isSelected ? 'transparent' :
         props.theme === TscThemeName.dawn && !props.isSelected ? TscStyles.color.ui.dawn.mediumDark :
            props.theme === TscThemeName.dusk ? TscStyles.color.text.medium :
               TscStyles.color.ui.dawn.mediumDark};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
   box-shadow: ${props => props.isReply ? 'none' : props.isSelected ? '0 5px 10px rgba(0,0,20,.1)' : '0 1px 4px rgba(0,0,20,.1)'};
   border-radius: 0.25rem;
   font-weight: 600;
   font-size: 0.875rem;
   animation: ${props => props.isHighlighted ? 'highlight-comment 2000ms ease backwards' : 'none'};
`;

const CommentAndRepliesWrapper = styled.li<{isReply: boolean}>`
   &:last-of-type {
      margin-bottom: ${props => props.isReply ? '0' : '1rem'};
   }
`;

const ExpandRepliesButton = styled.div`
   display: flex;

   button {
      padding-left: 0.125rem;

      span:first-of-type {
         margin-right: 0.375rem;
      }
   }
`;

const RepliesWrapper = styled.ol<{theme: TscThemeNames; replyCount: number}>`
   margin: ${props => props.replyCount > 0 ? '-0.5rem 0 0 2.25rem' : '0 0 0 2.25rem'};
   padding: 0 0 1.75rem 0;
   border-left: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.lightMedium : TscStyles.color.ui.dawn.mediumDark};
   list-style-type: none;

   .comment-wrapper:first-of-type {
      margin-top: 0.5rem;
   }
`;

export const CommentBase: React.FC<ICommentProps & IStateMappedProps> = ({comments, comment, isReply, shouldShowUpdate, setCommentsToHighlight, SelectedComment, VideoReviewId, annotationMode, theme, IsQuizzing, IsVideo}) => {
   const {t} = useTranslation();
   const [deleteAnnotationOnSave, setDeleteAnnotationOnSave] = useState(false);
   const [expandReplies, setExpandReplies] = useState(false);
   const [loadingState, setLoadingState] = SetupWorkerState();
   const [showDeleteAlertModal, setShowDeleteAlertModal] = useState(false);
   const isSelected = SelectedComment && (SelectedComment.id === comment.id || SelectedComment.id === comment.parentCommentId || comment.replies.includes(SelectedComment));
   const commentContainerRef = React.useRef(null);

   useEffect(() => {
      comment.replies.includes(SelectedComment) && setExpandReplies(true);
      SelectedComment?.id === comment.id && scrollCommentIntoView(commentContainerRef.current);
   }, [SelectedComment, comment]);

   useEffect(() => {
      !expandReplies && comment.isFiltered === false && !!comment.replies.filter(r => r.isFiltered === false).length && setExpandReplies(true);
   }, [comment.isFiltered, comment.replies]);

   useEffect(() => {
      shouldShowUpdate && !isReply && scrollCommentIntoView(commentContainerRef.current as HTMLElement);
   }, [shouldShowUpdate]);

   const submitReply = async (replyText: string) => {
      try {
         setLoadingState({isWorking: true, hasError: false});
         const commentsCopy = Object.assign([], comments);
         const newReply = Object.assign({},
            await conversationApi.addReply({
               text: replyText.trim(),
               annotation: '',
               originatingService: config.featureSwitches.IsScreencast ? ServiceNames.screencast : ServiceNames.knowmia
            }, comment),
            {
               user: {
                  ProfileImageUrl: config.user.AvatarImageUrl,
                  DisplayName: config.user.DisplayName,
                  Id: config.user.TechSmithId
               }
            }
         );

         if (config.featureSwitches.IsScreencast && config.user.IsSignedIn) {
            if (await conversationApi.isSubscribedToConversation(VideoReviewId) === null) {
               await conversationApi.subscribeToConversation(VideoReviewId);
            }
         }

         const updatedComment: IComment = Object.assign({}, commentsCopy.find(c => c.id === comment.id), {replies: comment.replies.concat(newReply), isFiltered: false});
         commentsCopy.splice(commentsCopy.indexOf(comment), 1, updatedComment);
         setCommentsToHighlight(commentsCopy, [newReply]);
         updateCommentsState({comments: commentsCopy});
         updateSelectedConversationState({SelectedComment: newReply});
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to submit reply: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const deleteComment = async (commentToDelete: IComment) => {
      setLoadingState({isWorking: true, hasError: false});

      try {
         const deletedComment = await conversationApi.deleteComment(commentToDelete);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentCommentIndex = commentsCopy.findIndex(c => c.id === deletedComment.parentCommentId);
            const replies = commentsCopy[parentCommentIndex].replies;
            const replyIndex = replies.findIndex((r: IComment) => r.id === deletedComment.id);

            if (parentCommentIndex > -1 && replyIndex > -1) {
               replies.splice(replyIndex, 1);
            } else {
               const errorText = parentCommentIndex > -1 ? 'Reply could not be found' : 'Parent comment could not be found';
               throw new Error(errorText);
            }

            if (replies.length === 0) {
               setExpandReplies(false);

               if (commentsCopy[parentCommentIndex].isDeleted) {
                  commentsCopy.splice(parentCommentIndex, 1);
               }
            }
         } else {
            const commentIndex = commentsCopy.findIndex((oldComment: IComment) => oldComment.id === deletedComment.id);

            if (commentIndex > -1) {
               if (comments[commentIndex].replies.length) {
                  commentsCopy[commentIndex] = Object.assign({}, comments[commentIndex], {text: t('conversations.commentDeletedText'), annotation: '', isDeleted: true, isEditing: false});
               } else {
                  commentsCopy.splice(commentIndex, 1);
               }
               updateAnnotationManagerState({annotationMode: AnnotationModes.none});
            } else {
               throw new Error('Comment could not be found');
            }
         }

         setCommentsToHighlight(commentsCopy, []);
         updateCommentsState({comments: commentsCopy});
         setDeleteAnnotationOnSave(false);
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to delete comment: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const saveCommentEdits = async (editedText: string) => {
      const updatedComment = Object.assign({}, comment, {text: editedText.trim(), isEditing: false, annotation: deleteAnnotationOnSave ? '' : comment.annotation});

      if (!updatedComment.text && !updatedComment.annotation) {
         setShowDeleteAlertModal(true);
         return;
      } else if (!updatedComment.text) {
         updatedComment.text = t('conversations.annotationAttached');
      }

      try {
         setLoadingState({isWorking: true, hasError: false});
         updatedComment.text = editedText;
         const editedComment = await conversationApi.editComment(updatedComment);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentCommentIndex = commentsCopy.findIndex(c => c.id === updatedComment.parentCommentId);
            const replies = commentsCopy[parentCommentIndex].replies;
            const replyIndex = replies.findIndex((r: IComment) => r.id === updatedComment.id);
            (parentCommentIndex > -1 && replyIndex > -1) && replies.splice(replyIndex, 1, editedComment);
         } else {
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === editedComment.id), 1, editedComment);
         }

         updateCommentsState({comments: commentsCopy});
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to edit comment: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const selectAndPause = () => {
      const timeDifference = TimeFormatter.getRoundedTime(Math.abs(mediaInterface.currentTime - comment.time));
      const timeMismatch = IsVideo && timeDifference > Constants.annotation.timeTolerance;

      if (!isSelected || timeMismatch || (SelectedComment && SelectedComment?.id !== comment.id) || annotationMode === AnnotationModes.edit) {
         const topLevelComment = isReply ? comments.find(c => c.id === comment.parentCommentId) : comment;
         updateSelectedConversationState({SelectedComment: comment});
         updateAnnotationManagerState({annotationMode: topLevelComment.annotation ? AnnotationModes.display : AnnotationModes.none});

         if (!IsQuizzing && timeMismatch) {
            mediaInterface.pause();
            mediaInterface.currentTime = comment.time;
         }
      }
   };

   const commentRepliesLength = comment.replies.filter(r => !r.isFiltered).length;
   let expandThreadText: string = expandReplies ? t('conversations.commentReplyThread.hide') : t('conversations.commentReplyThread.show');
   expandThreadText = `${expandThreadText} ${commentRepliesLength} ${commentRepliesLength > 1 ? t('conversations.commentReplyThread.replies') : t('conversations.commentReplyThread.reply')}`;
   const isCommentOwner = comment.authorId === config.user.TechSmithId;

   const onClickExpandReplies: MouseEventHandler = event => {
      event.stopPropagation();
      setExpandReplies(!expandReplies);
   };

   const wrapperClassName = isSelected ? 'comment-wrapper selected-comment' : 'comment-wrapper';

   return (
      comment.isFiltered ? null :
         <CommentAndRepliesWrapper isReply={isReply}>
            <CommentWrapper className={wrapperClassName} data-test-id="comment-wrapper" ref={commentContainerRef} theme={theme} isReply={isReply} isExpanded={expandReplies} isSelected={isSelected} onClick={selectAndPause} isHighlighted={!isReply && shouldShowUpdate}>
               <CommentHeader isReply={isReply} comment={comment} deleteAnnotationOnSave={deleteAnnotationOnSave} isSelected={isSelected} setDeleteAnnotationOnSave={setDeleteAnnotationOnSave} onCommentClick={selectAndPause} />
               {loadingState.isWorking && <Spinner themeName={TscThemeName.dawn} />}
               {showDeleteAlertModal && <DeleteCommentAlert setShowDeleteAlert={setShowDeleteAlertModal} onClose={() => setShowDeleteAlertModal(false)} comment={comment} deleteComment={deleteComment}/>}
               {!loadingState.isWorking && <CommentBody setDeleteAnnotationOnSave={setDeleteAnnotationOnSave} deleteAnnotationOnSave={deleteAnnotationOnSave} handleExpandReplies={() => setExpandReplies(!comment.replies.length ? !expandReplies : true)} isCommentOwner={isCommentOwner} comment={comment} isReply={isReply} saveEdit={(editedText: string) => saveCommentEdits(editedText)} loadingState={loadingState} setShowDeleteAlertModal={setShowDeleteAlertModal} />}
               {!!commentRepliesLength &&
                  <ExpandRepliesButton>
                     <ControlButton
                        buttonType={TscButtonType.floating}
                        onClick={onClickExpandReplies}
                        label={expandThreadText}
                        glyph={expandReplies ? <Glyphs.ArrowUp16x16/> : <Glyphs.ArrowDown16x16/>}
                        glyphPlacement={TscButtonGlyphPlacement.left}
                        themeName={theme}
                        testId="expand-comment-replies-button"
                        ariaExpanded={expandReplies}
                     />
                  </ExpandRepliesButton>
               }
            </CommentWrapper>
            {(expandReplies && !isReply) &&
               <>
                  <RepliesWrapper theme={theme} replyCount={commentRepliesLength}>
                     {!!commentRepliesLength && comment.replies.map((reply: IComment, index: number) => <CommentBase theme={theme} key={index} isReply={true} comment={reply} shouldShowUpdate={shouldShowUpdate} setCommentsToHighlight={setCommentsToHighlight} SelectedComment={SelectedComment} VideoReviewId={VideoReviewId} comments={comments} annotationMode={annotationMode} IsQuizzing={IsQuizzing} IsVideo={IsVideo} />)}
                  </RepliesWrapper>
                  <CommentInput hasAnnotation={false} inputId={`${comment.id}-reply-input`} setDeleteAnnotationOnSave={setDeleteAnnotationOnSave} isCommentOwner={isCommentOwner} isReply={true} onSubmit={submitReply} loadingState={loadingState} />
               </>
            }
         </CommentAndRepliesWrapper>
   );
};

export interface ICommentProps {
   comment: IComment;
   isReply: boolean;
   shouldShowUpdate: boolean;
   setCommentsToHighlight(prevComments: IComment[], newComments: IComment[]): void;
}

export interface IStateMappedProps {
   SelectedComment: IComment;
   VideoReviewId: string;
   comments: IComment[];
   annotationMode: AnnotationModes;
   theme: TscThemeNames;
   IsQuizzing: boolean;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {comment, isReply, shouldShowUpdate, setCommentsToHighlight}: ICommentProps,
   {SelectedComment, VideoReviewId}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {annotationMode}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>,
   {IsQuizzing}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): ICommentProps & IStateMappedProps => ({comments, comment, isReply, shouldShowUpdate, setCommentsToHighlight, SelectedComment, VideoReviewId, annotationMode, theme, IsQuizzing, IsVideo});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, commentsStore, annotationManagerStore, themeStore, quizDataStore, mediaStore)(CommentBase);
